import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowRight, Star, Award, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { portfolioItems } from '../mockData';

const Portfolio = () => {
  // const [activeFilter, setActiveFilter] = useState('All'); // Removed as filtering logic is removed

  // const categories = ['All', 'Salon', 'Real Estate', 'Travel', 'Coaching', 'Personal Branding', 'Product Branding']; // Removed as filtering logic is removed
  const filteredItems = portfolioItems; // Always show all items

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Portfolio - Arikone | Digital Marketing Success Stories & Case Studies"
        description="Explore our portfolio of successful digital marketing campaigns for salons, real estate, travel agencies, coaching institutes, and branding projects. See real results from Arikone's marketing expertise."
        keywords="digital marketing portfolio, marketing case studies, successful campaigns, salon marketing, real estate marketing, travel agency marketing, coaching marketing, brand success stories"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-20 lg:py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-[1000px] h-[1000px] rounded-full bg-purple-500/10 blur-3xl animate-pulse" />
          <div className="absolute -bottom-1/2 -left-1/2 w-[800px] h-[800px] rounded-full bg-blue-500/10 blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left Content */}
            <div className="text-left animate-fadeInUp">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 mb-6 backdrop-blur-sm">
                <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
                Key Achievements
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Our <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Success Stories
                </span>
              </h1>

              <p className="text-xl text-blue-100 mb-8 max-w-xl leading-relaxed">
                Explore how we've helped businesses transform their digital presence and achieve measurable growth through strategic marketing.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-blue-600/25 transition-all hover:scale-105"
                >
                  <Link to="/contact">
                    Start Your Project <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Visuals */}
            <div className="relative hidden lg:block h-[500px]">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-purple-600/20 rounded-full blur-3xl" />

              {/* Floating Icons Grid/Cloud */}
              <div className="relative w-full h-full">
                {/* Center Piece */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-2xl shadow-2xl animate-float">
                  <div className="text-center">
                    <span className="block text-5xl mb-2">🏆</span>
                    <div className="text-2xl font-bold text-white">Award Winning</div>
                    <div className="text-sm text-blue-200">Strategies</div>
                  </div>
                </div>

                {/* Orbiting Elements */}
                <div className="absolute top-10 right-10 bg-blue-500/20 backdrop-blur-md p-4 rounded-xl border border-blue-400/30 animate-bounce delay-100 shadow-lg shadow-blue-500/20">
                  <TrendingUp className="text-blue-300 w-8 h-8" />
                </div>
                <div className="absolute bottom-20 left-10 bg-purple-500/20 backdrop-blur-md p-4 rounded-xl border border-purple-400/30 animate-bounce delay-300 shadow-lg shadow-purple-500/20">
                  <Star className="text-purple-300 w-8 h-8" />
                </div>
                <div className="absolute top-20 left-0 bg-indigo-500/20 backdrop-blur-md p-4 rounded-xl border border-indigo-400/30 animate-bounce delay-500 shadow-lg shadow-indigo-500/20">
                  <Award className="text-indigo-300 w-8 h-8" />
                </div>
                <div className="absolute bottom-0 right-20 bg-cyan-500/20 backdrop-blur-md p-4 rounded-xl border border-cyan-400/30 animate-bounce delay-700 shadow-lg shadow-cyan-500/20">
                  <Briefcase className="text-cyan-300 w-8 h-8" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section was here, now removed */}

      {/* Portfolio Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => {
              const CardContentWrapper = ({ children }) => (
                <Card
                  className={`overflow-hidden hover-glow cursor-pointer animate-fadeInUp stagger-${(index % 6) + 1} h-full flex flex-col`}
                >
                  {children}
                </Card>
              );

              const content = (
                <>
                  <div className="relative h-64 overflow-hidden shrink-0">
                    <img
                      src={item.image}
                      alt={`${item.title} - ${item.category} digital marketing case study`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {item.category}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6 flex flex-col grow">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                      {item.title}
                      {item.link && <ArrowRight className="ml-2 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </h3>
                    <p className="text-gray-600 mb-4 grow">{item.description}</p>
                    <div className="pt-4 border-t border-gray-200 mt-auto">
                      <p className="text-sm font-semibold text-blue-600">{item.results}</p>
                    </div>
                  </CardContent>
                </>
              );

              return (
                <div key={item.id} className="group h-full">
                  {item.link ? (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="block h-full">
                      <CardContentWrapper>{content}</CardContentWrapper>
                    </a>
                  ) : (
                    <CardContentWrapper>{content}</CardContentWrapper>
                  )}
                </div>
              );
            })}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Impact</h2>
            <p className="text-xl text-blue-100">Real results that speak for themselves</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '₹150Cr+', label: 'Revenue Generated' },
              { value: '250%', label: 'Average ROI' },
              { value: '500+', label: 'Campaigns Launched' },
              { value: '98%', label: 'Client Satisfaction' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-blue-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;